import styles from 'css/src/components/video-planner/VideoDraftTile.module.scss';

import React, { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { CanvasPreview } from 'js/src/components/common/ui/CanvasPreview';
import { ContentPlaceholderBlock } from 'js/src/components/common/ui/ContentPlaceholder';
import { VideoDraftLoadingProgress } from 'js/src/components/video-planner/VideoDraftLoadingProgress';
import { VideoInfoChips } from 'js/src/components/video-planner/VideoInfoChips';
import { getVideoDraftById } from 'js/src/data/video-planner';
import { type RootState } from 'js/src/libs/RootState';

export const VideoDraftTile = React.memo(
    ({
        videoDraftId,
        isSelected,
        onSelect,
    }: {
        videoDraftId: string;
        isSelected: boolean;
        onSelect: (videoDraftId: string) => void;
    }) => {
        const videoDraft = useSelector((state: RootState) =>
            getVideoDraftById(state, videoDraftId),
        );
        if (!videoDraft) throw new Error('Missing video draft');

        const previewCanvas = videoDraft.preview_canvas;
        const isBlankVideo = videoDraft.creationToolName === 'create_blank_video';

        const tileRef = useRef<HTMLDivElement>(null);
        const onCardClick = useCallback(() => {
            if (!isSelected) {
                analytics.track('Sketch: Video Draft Selected');

                onSelect(videoDraftId);

                setTimeout(() => {
                    if (tileRef.current) {
                        tileRef.current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                            inline: 'center',
                        });
                    }
                }, 100);
            }
        }, [isSelected, onSelect, videoDraftId]);

        const aspectRatio = videoDraft.luminary.aspectRatio || '';

        const previewNode: React.ReactNode = (() => {
            if (previewCanvas) {
                return (
                    <div className={`${styles['video-draft-preview']} ${aspectRatio}`}>
                        <div className="position-relative w-100 h-100 d-flex align-items-center justify-content-center">
                            <CanvasPreview
                                className="h-100 w-100 position-absolute"
                                sourceCanvas={previewCanvas}
                            />
                            {!isSelected && (
                                <div className="video-preview">
                                    <div className="video-preview-overlay d-flex justify-content-center align-items-center position-absolute top-0 start-0 bottom-0 end-0 w-100">
                                        <div className="text-center video-preview-play-pause p-l5-8">
                                            <span
                                                translate="no"
                                                className="material-symbols-outlined material-symbols-f1"
                                            >
                                                play_arrow
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            }
            return (
                <div className={`${styles['video-draft-preview']} ${aspectRatio}`}>
                    <ContentPlaceholderBlock />
                </div>
            );
        })();

        if (videoDraft.error_message) {
            return null;
        }

        return (
            <div
                ref={tileRef}
                className={classNames(styles['video-draft-tile'], {
                    [styles['is-selected']]: isSelected,
                })}
            >
                <div
                    className={`${styles['video-draft-card']} ${aspectRatio}`}
                    onClick={onCardClick}
                >
                    <div className={`${styles['video-draft-preview-container']} ${aspectRatio}`}>
                        {previewNode}
                    </div>

                    <div className={styles['video-draft-content']}>
                        {videoDraft.isGenerating && !isBlankVideo && (
                            <VideoDraftLoadingProgress videoDraftId={videoDraftId} />
                        )}
                    </div>
                </div>

                {!isBlankVideo && (
                    <VideoInfoChips videoDraftId={videoDraftId} isActive={isSelected} />
                )}
            </div>
        );
    },
);

VideoDraftTile.displayName = 'VideoDraftTile';
